import { useState } from 'react';
import { Activity, Gauge, Clock, ChevronDown, ChevronUp, Zap } from 'lucide-react';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import type { StringCurrent, StringCharts } from '@/api/stringDetail';
import type { PowerPoint } from '@/api/inverterDetail';

type Props = {
  current: StringCurrent;
  ipv: StringCharts;
  upv: StringCharts;
  pac: StringCharts;
};

type TimeTab = 'hourly' | 'daily' | 'monthly' | 'yearly';

const TIME_TABS: { key: TimeTab; label: string }[] = [
  { key: 'hourly',  label: 'Saatlik' },
  { key: 'daily',   label: 'Günlük' },
  { key: 'monthly', label: 'Aylık' },
  { key: 'yearly',  label: 'Yıllık' },
];

function TimeTabBar({ active, onChange }: { active: TimeTab; onChange: (t: TimeTab) => void }) {
  return (
    <div className="flex rounded-lg border border-gray-200 overflow-hidden text-xs font-medium">
      {TIME_TABS.map(t => (
        <button
          key={t.key}
          onClick={() => onChange(t.key)}
          className={`px-3 py-1.5 transition-colors ${
            active === t.key ? 'bg-blue-600 text-white' : 'text-gray-500 hover:bg-gray-50'
          }`}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}

function MiniChart({ data, color, type }: { data: PowerPoint[]; color: string; type: 'bar' | 'line' }) {
  const pts = data.map(d => ({ date_name: d.date_name, power_total: parseFloat(String(d.power_total)) || 0 }));
  const maxVal = Math.max(...pts.map(d => d.power_total), 0);

  return (
    <ResponsiveContainer width="100%" height={160}>
      {type === 'line' ? (
        <LineChart data={pts} margin={{ top: 4, right: 8, left: 0, bottom: 30 }}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
          <XAxis dataKey="date_name" tick={{ fontSize: 9, fill: '#6b7280' }} angle={-45} textAnchor="end" interval="preserveStartEnd" />
          <YAxis tick={{ fontSize: 9, fill: '#6b7280' }} width={44} domain={[0, Math.ceil(maxVal * 1.1) || 1]} />
          <Tooltip formatter={(v) => [v, '']} contentStyle={{ fontSize: 11, borderRadius: 6 }} />
          <Line type="monotone" dataKey="power_total" stroke={color} dot={false} strokeWidth={2} />
        </LineChart>
      ) : (
        <BarChart data={pts} margin={{ top: 4, right: 8, left: 0, bottom: 30 }}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
          <XAxis dataKey="date_name" tick={{ fontSize: 9, fill: '#6b7280' }} angle={-45} textAnchor="end" interval="preserveStartEnd" />
          <YAxis tick={{ fontSize: 9, fill: '#6b7280' }} width={44} domain={[0, Math.ceil(maxVal * 1.1) || 1]} />
          <Tooltip formatter={(v) => [v, '']} contentStyle={{ fontSize: 11, borderRadius: 6 }} />
          <Bar dataKey="power_total" fill={color} radius={[3, 3, 0, 0]} maxBarSize={28} />
        </BarChart>
      )}
    </ResponsiveContainer>
  );
}

function Section({ title, children, defaultOpen = false }: { title: string; children: React.ReactNode; defaultOpen?: boolean }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div>
      <button
        onClick={() => setOpen(p => !p)}
        className="flex w-full items-center justify-between rounded-md bg-gray-100 px-3 py-2 text-sm font-semibold hover:bg-gray-200"
      >
        <span>{title}</span>
        {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
      </button>
      {open && <div className="mt-2">{children}</div>}
    </div>
  );
}

function StatRow({ label, value, unit }: { label: string; value: number | null | undefined; unit: string }) {
  const display = value != null ? `${value} ${unit}` : '--';
  return (
    <div className="flex justify-between items-center py-1 border-b border-gray-100 last:border-0">
      <span className="text-xs text-gray-500">{label}</span>
      <span className="text-xs font-semibold">{display}</span>
    </div>
  );
}

export default function StringDetailCard({ current: s, ipv, upv, pac }: Props) {
  const [ipvTab, setIpvTab] = useState<TimeTab>('daily');
  const [upvTab, setUpvTab] = useState<TimeTab>('daily');
  const [pacTab, setPacTab] = useState<TimeTab>('daily');

  const isOnline = s.inverter_string_status === 1;
  const effPct = s.inverter_string_efficiency_according_to_other_strings != null
    ? Math.round(s.inverter_string_efficiency_according_to_other_strings * 100)
    : null;
  const twinEffPct = s.inverter_string_twin_string_efficiency_according_to_other_strings != null
    ? Math.round(s.inverter_string_twin_string_efficiency_according_to_other_strings * 100)
    : null;
  const twinRatio = s.inverter_string_efficiency_according_to_twin_string;

  return (
    <div className="space-y-4 rounded-2xl bg-white p-4 shadow-md">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-gray-500">{s.inverter_string_inverter_brand} {s.inverter_string_inverter_model}</p>
        </div>
        <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${
          isOnline ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'
        }`}>
          {isOnline ? 'Online' : 'Offline'}
        </span>
      </div>

      {/* Actual values */}
      <div className="grid grid-cols-2 gap-3">
        <div className="rounded-lg bg-gray-50 p-3 flex flex-col gap-1">
          <div className="flex items-center gap-1 text-xs text-gray-500"><Activity size={13} /><span>Akım</span></div>
          <span className="text-xl font-bold">
            {s.inverter_string_actual_current != null ? `${s.inverter_string_actual_current} A` : '--'}
          </span>
        </div>
        <div className="rounded-lg bg-gray-50 p-3 flex flex-col gap-1">
          <div className="flex items-center gap-1 text-xs text-gray-500"><Gauge size={13} /><span>Gerilim</span></div>
          <span className="text-xl font-bold">
            {s.inverter_string_actual_voltage != null ? `${s.inverter_string_actual_voltage} V` : '--'}
          </span>
        </div>
      </div>

      {/* Averages */}
      <Section title="Ortalama Veriler">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-xs font-semibold text-gray-600 mb-1">Akım Ortalaması (A)</p>
            <StatRow label="Günlük"  value={s.inverter_string_daily_current_avg}   unit="A" />
            <StatRow label="Aylık"   value={s.inverter_string_monthly_current_avg} unit="A" />
            <StatRow label="Yıllık"  value={s.inverter_string_yearly_current_avg}  unit="A" />
          </div>
          <div>
            <p className="text-xs font-semibold text-gray-600 mb-1">Gerilim Ortalaması (V)</p>
            <StatRow label="Günlük"  value={s.inverter_string_daily_voltage_avg}   unit="V" />
            <StatRow label="Aylık"   value={s.inverter_string_monthly_voltage_avg} unit="V" />
            <StatRow label="Yıllık"  value={s.inverter_string_yearly_voltage_avg}  unit="V" />
          </div>
        </div>
      </Section>

      {/* Twin string */}
      {s.inverter_string_twin_string_number != null && (
        <Section title={`Twin String (String ${s.inverter_string_twin_string_number})`}>
          <div className="grid grid-cols-2 gap-3 mb-2">
            <div className="rounded-lg bg-gray-50 p-2 text-center">
              <p className="text-xs text-gray-500">Akım</p>
              <p className="text-sm font-semibold">
                {s.inverter_string_twin_string_actual_current != null ? `${s.inverter_string_twin_string_actual_current} A` : '--'}
              </p>
            </div>
            <div className="rounded-lg bg-gray-50 p-2 text-center">
              <p className="text-xs text-gray-500">Gerilim</p>
              <p className="text-sm font-semibold">
                {s.inverter_string_twin_string_actual_voltage != null ? `${s.inverter_string_twin_string_actual_voltage} V` : '--'}
              </p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-xs font-semibold text-gray-600 mb-1">Twin Akım Ort. (A)</p>
              <StatRow label="Günlük"  value={s.inverter_string_twin_string_daily_current_avg}   unit="A" />
              <StatRow label="Aylık"   value={s.inverter_string_twin_string_monthly_current_avg} unit="A" />
              <StatRow label="Yıllık"  value={s.inverter_string_twin_string_yearly_current_avg}  unit="A" />
            </div>
            <div>
              <p className="text-xs font-semibold text-gray-600 mb-1">Twin Gerilim Ort. (V)</p>
              <StatRow label="Günlük"  value={s.inverter_string_twin_string_daily_voltage_avg}   unit="V" />
              <StatRow label="Aylık"   value={s.inverter_string_twin_string_monthly_voltage_avg} unit="V" />
              <StatRow label="Yıllık"  value={s.inverter_string_twin_string_yearly_voltage_avg}  unit="V" />
            </div>
          </div>
        </Section>
      )}

      {/* Efficiency */}
      <Section title="Verimlilik">
        <div className="grid grid-cols-2 gap-3">
          <div className="rounded-lg bg-gray-50 p-3 text-center">
            <div className="flex items-center justify-center gap-1 text-xs text-gray-500 mb-1"><Zap size={12} /><span>Diğer Stringlere Göre</span></div>
            <span className={`text-lg font-bold ${
              effPct == null ? 'text-gray-400'
              : effPct >= 80 ? 'text-emerald-600'
              : effPct >= 50 ? 'text-yellow-600'
              : 'text-rose-600'
            }`}>
              {effPct != null ? `${effPct}%` : '--'}
            </span>
          </div>
          {s.inverter_string_twin_string_number != null && (
            <div className="rounded-lg bg-gray-50 p-3 text-center">
              <div className="flex items-center justify-center gap-1 text-xs text-gray-500 mb-1"><Zap size={12} /><span>Twin&apos;e Göre</span></div>
              <span className={`text-lg font-bold ${
                twinRatio == null ? 'text-gray-400'
                : twinRatio >= 0.9 ? 'text-emerald-600'
                : twinRatio >= 0.7 ? 'text-yellow-600'
                : 'text-rose-600'
              }`}>
                {twinRatio != null ? `${Math.round(twinRatio * 100)}%` : '--'}
              </span>
              {twinEffPct != null && (
                <p className="text-xs text-gray-400 mt-0.5">Twin: {twinEffPct}%</p>
              )}
            </div>
          )}
        </div>
      </Section>

      {/* IPV Chart */}
      <Section title="Akım Geçmişi (A)" defaultOpen>
        <div className="flex justify-end mb-2">
          <TimeTabBar active={ipvTab} onChange={setIpvTab} />
        </div>
        <MiniChart data={ipv[ipvTab]} color="#a855f7" type="line" />
      </Section>

      {/* UPV Chart */}
      <Section title="Gerilim Geçmişi (V)">
        <div className="flex justify-end mb-2">
          <TimeTabBar active={upvTab} onChange={setUpvTab} />
        </div>
        <MiniChart data={upv[upvTab]} color="#14b8a6" type="line" />
      </Section>

      {/* PAC Chart */}
      <Section title="Güç Geçmişi (kW)">
        <div className="flex justify-end mb-2">
          <TimeTabBar active={pacTab} onChange={setPacTab} />
        </div>
        <MiniChart data={pac[pacTab]} color="#3b82f6" type="bar" />
      </Section>

      {/* Last seen */}
      <div className="flex items-center justify-between text-xs text-gray-400">
        <div className="flex items-center gap-1"><Clock size={12} /><span>Son veri</span></div>
        <span>{s.inverter_string_last_seen ?? '--'}</span>
      </div>
    </div>
  );
}
